/**
 * contourSlice — Redux Toolkit state for the Auto-Contour Pipeline.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   uploadStatus: UploadStatus,            // idle | uploading | complete | error
 *   sourceImageUrl: string | null,         // object URL or CDN URL for the source image
 *   backgroundRemovalMode: BackgroundRemovalMode, // hint sent to the server pipeline (SF-CONTOUR-106)
 *   clientContourPathJson: Record<string, unknown>[] | null, // Paper.js path data
 *   borderThickness: number,               // inches, 0–0.25 (server allows up to 1)
 *   borderColorHex: string,                // #rrggbb
 *   cornerSmoothing: number,               // 0–1
 * }
 * ```
 *
 * Side effects (toasts, navigation) belong in the consuming container — never here.
 *
 * SF-CONTOUR-002.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ContourPreviewStatus } from '@dfq/shared/types/contourPreview';
import type {
  BackgroundRemovalMode,
  ClientContourPathJson,
  ContourCornerStyle,
} from '@dfq/shared/types/contour';

// ============================================================================
// Types — exported so capsules / containers reference them without importing
// the raw state shape.
// ============================================================================

export type UploadStatus = 'idle' | 'uploading' | 'complete' | 'error';

export type { BackgroundRemovalMode, ClientContourPathJson, ContourCornerStyle };

export interface ContourState {
  readonly uploadStatus: UploadStatus;
  readonly sourceImageUrl: string | null;
  readonly backgroundRemovalMode: BackgroundRemovalMode;
  readonly clientContourPathJson: ClientContourPathJson | null;
  readonly borderThickness: number;
  readonly borderColorHex: string;
  readonly cornerSmoothing: number;
  /**
   * Corner treatment applied when offsetting the auto-traced cut path
   * (SH-SHAPE-102). Initialized from the admin-configured default by the
   * container that mounts the corner-style control, not a literal here.
   */
  readonly cornerStyle: ContourCornerStyle;
  /**
   * Client-side re-offset of `clientContourPathJson.primary` using the
   * current `cornerStyle`/`cornerSmoothing`, computed by
   * `useContourOffsetPreview` via js-angusj-clipper. Lets the corner-style
   * picker preview instantly without re-running background removal or
   * OpenCV contour detection (SH-SHAPE-102 — "re-offsets without re-tracing").
   * `null` until a detected contour and a style selection both exist.
   */
  readonly offsetPreviewPathJson: ClientContourPathJson | null;
  /** Server id of the uploaded artwork file, set by the contour flow when its upload resolves. */
  readonly artworkFileId: number | null;
  /** Server preview the customer is looking at; sent as `contour_preview_id` at add-to-cart. */
  readonly contourPreviewId: number | null;
  readonly contourPreviewStatus: ContourPreviewStatus | null;
  /** True once the customer confirmed the current succeeded preview; any change to the preview clears it. */
  readonly contourPreviewConfirmed: boolean;
  /** Physical width of the contour output in inches; null until a contour output size is known. */
  readonly widthInches: number | null;
  /** Physical height of the contour output in inches; null until a contour output size is known. */
  readonly heightInches: number | null;
}

// ============================================================================
// Initial state
// ============================================================================

export const initialState: ContourState = {
  uploadStatus: 'idle',
  sourceImageUrl: null,
  backgroundRemovalMode: 'auto',
  clientContourPathJson: null,
  borderThickness: 0.1,
  borderColorHex: '#ffffff',
  cornerSmoothing: 0,
  cornerStyle: 'as_is',
  offsetPreviewPathJson: null,
  artworkFileId: null,
  contourPreviewId: null,
  contourPreviewStatus: null,
  contourPreviewConfirmed: false,
  widthInches: null,
  heightInches: null,
};

// ============================================================================
// Slice
// ============================================================================

const contourSlice = createSlice({
  name: 'contour',
  initialState,
  reducers: {
    setUploadStatus(state, action: PayloadAction<UploadStatus>) {
      state.uploadStatus = action.payload;
    },

    setSourceImageUrl(state, action: PayloadAction<string | null>) {
      state.sourceImageUrl = action.payload;
    },

    setBackgroundRemovalMode(state, action: PayloadAction<BackgroundRemovalMode>) {
      state.backgroundRemovalMode = action.payload;
    },

    setClientContourPathJson(
      state,
      action: PayloadAction<ClientContourPathJson | null>,
    ) {
      state.clientContourPathJson = action.payload;
    },

    setBorderThickness(state, action: PayloadAction<number>) {
      state.borderThickness = action.payload;
    },

    setBorderColorHex(state, action: PayloadAction<string>) {
      state.borderColorHex = action.payload;
    },

    setCornerSmoothing(state, action: PayloadAction<number>) {
      state.cornerSmoothing = action.payload;
    },

    /** SH-SHAPE-102 — select as_is / rounded / sharp for auto-trace offsetting. */
    setCornerStyle(state, action: PayloadAction<ContourCornerStyle>) {
      state.cornerStyle = action.payload;
    },

    /**
     * Stores the client-computed re-offset preview (SH-SHAPE-102). Dispatched
     * by `useContourOffsetPreview` whenever `clientContourPathJson`,
     * `cornerStyle`, `cornerSmoothing`, or `borderThickness` change — never
     * touches `clientContourPathJson` itself, so the underlying trace is
     * preserved for the next re-offset.
     */
    setOffsetPreviewPathJson(state, action: PayloadAction<ClientContourPathJson | null>) {
      state.offsetPreviewPathJson = action.payload;
    },

    /** Store the artwork file id received after the in-memory file is uploaded. */
    setArtworkFileId(state, action: PayloadAction<number | null>) {
      state.artworkFileId = action.payload;
    },

    /** Mirrors the polled server preview so Confirm / add-to-cart can gate on it. */
    setContourPreview(
      state,
      action: PayloadAction<{ id: number | null; status: ContourPreviewStatus | null }>,
    ) {
      if (
        state.contourPreviewId !== action.payload.id ||
        state.contourPreviewStatus !== action.payload.status
      ) {
        state.contourPreviewConfirmed = false;
      }
      state.contourPreviewId = action.payload.id;
      state.contourPreviewStatus = action.payload.status;
    },

    /** Server-detected artwork size; both null (cleared) or both positive finite numbers. */
    setContourDetectedSize(
      state,
      action: PayloadAction<{ widthInches: number; heightInches: number } | null>,
    ) {
      state.widthInches = action.payload?.widthInches ?? null;
      state.heightInches = action.payload?.heightInches ?? null;
    },

    setContourPreviewConfirmed(state, action: PayloadAction<boolean>) {
      state.contourPreviewConfirmed = action.payload;
    },

    /** Reset the entire slice to initial state. */
    resetContour() {
      return initialState;
    },
  },

});

export const {
  setUploadStatus,
  setSourceImageUrl,
  setBackgroundRemovalMode,
  setClientContourPathJson,
  setBorderThickness,
  setBorderColorHex,
  setCornerSmoothing,
  setCornerStyle,
  setOffsetPreviewPathJson,
  setArtworkFileId,
  setContourPreview,
  setContourDetectedSize,
  setContourPreviewConfirmed,
  resetContour,
} = contourSlice.actions;

export { contourSlice };
export const contourReducer = contourSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

type StateWithContour = { contour: ContourState };

export const selectUploadStatus = (state: StateWithContour): UploadStatus =>
  state.contour.uploadStatus;

export const selectSourceImageUrl = (state: StateWithContour): string | null =>
  state.contour.sourceImageUrl;

export const selectBackgroundRemovalMode = (
  state: StateWithContour,
): BackgroundRemovalMode => state.contour.backgroundRemovalMode;

export const selectClientContourPathJson = (
  state: StateWithContour,
): ClientContourPathJson | null => state.contour.clientContourPathJson;

export const selectBorderThickness = (state: StateWithContour): number =>
  state.contour.borderThickness;

export const selectBorderColorHex = (state: StateWithContour): string =>
  state.contour.borderColorHex;

export const selectCornerSmoothing = (state: StateWithContour): number =>
  state.contour.cornerSmoothing;

export const selectCornerStyle = (state: StateWithContour): ContourCornerStyle =>
  state.contour.cornerStyle;

export const selectOffsetPreviewPathJson = (
  state: StateWithContour,
): ClientContourPathJson | null => state.contour.offsetPreviewPathJson;

export const selectArtworkFileId = (state: StateWithContour): number | null =>
  state.contour.artworkFileId;

export const selectContourPreviewId = (state: StateWithContour): number | null =>
  state.contour.contourPreviewId;

export const selectContourPreviewStatus = (
  state: StateWithContour,
): ContourPreviewStatus | null => state.contour.contourPreviewStatus;

export const selectContourPreviewConfirmed = (state: StateWithContour): boolean =>
  state.contour.contourPreviewConfirmed;

export const selectContourWidthInches = (state: StateWithContour): number | null =>
  state.contour.widthInches;

export const selectContourHeightInches = (state: StateWithContour): number | null =>
  state.contour.heightInches;
